import React, { useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import config from 'config';
import bsv from 'bsv';
import MoneyButton from '@moneybutton/react-money-button';
import classNames from 'classnames/bind';
import sha1 from 'sha1';
import styles from './styles.scss';
import useDebouncedState from '../../../helpers/useDebouncedState';
import useSettings from '../hooks/useSettings';
import { useGameStore } from '../hooks/useGameStore';
import useAppStore from '../../../useAppStore';
import useDepositSettings from './useDepositSettings';
import useAffiliateScript from '../../../games/hooks/useAffiliateScript';

const cx = classNames.bind(styles);

const buttonWidth = 145;

export default function MoneyButtonWrapper({
  disabled, bettingData, onBetSuccess, setLoaded, hidden, isDeposit,
}) {
  const affiliateScript = useAffiliateScript();
  const { address: receiver } = useSettings({ enabled: !isDeposit });
  const { receiver: depositAddress } = useDepositSettings(isDeposit);
  const [debouncedBettingData, setBettingData] = useDebouncedState(bettingData, 400);
  const { amount, script, multiplier } = debouncedBettingData;
  const containerRef = useRef();

  useEffect(() => {
    useGameStore.getState().setBettingDisabled(true);
    setLoaded(false);
  }, []);

  function onPayment(transactionData) {
    const user = useAppStore.getState().user.data;
    const hashedId = sha1(`${transactionData.userId}@moneybutton.com`);

    if (!user?.hashedId !== hashedId) {
      useAppStore.getState().setUserData({ hashedId });
    }
    onBetSuccess(transactionData);
  }

  const actualScript = script + affiliateScript;
  const actualAmount = amount.multipliedBy(multiplier).toFixed();
  const memoizedMoneyButton = useMemo(
    () => (
      <MoneyButton
        buttonId="money-button"
        clientIdentifier={config.moneyButtonClientIdentifier}
        onPayment={onPayment}
        onError={console.error}
        onLoad={() => setLoaded(true)}
        outputs={[
          {
            script: bsv.Script.buildSafeDataOut([actualScript]).toASM(),
            amount: '0',
            currency: 'BSV',
          },
          { to: isDeposit ? depositAddress : receiver, amount: actualAmount, currency: 'BSV' },
        ]}
        type="tip"
        disabled={disabled}
        successMessage="Good Luck!"
      />
    ),
    [debouncedBettingData, disabled, isDeposit]
  );

  useEffect(() => {
    setBettingData(bettingData);
  }, [bettingData]);

  return (
    <div
      className={cx('moneybutton-container', { hidden })}
      style={{
        paddingLeft: containerRef.current?.getBoundingClientRect().width / 2 - buttonWidth / 2 || 0,
      }}
      ref={containerRef}
    >
      {memoizedMoneyButton}
    </div>
  );
}
MoneyButtonWrapper.propTypes = {
  bettingData: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  onBetSuccess: PropTypes.func.isRequired,
  hidden: PropTypes.bool,
  setLoaded: PropTypes.func.isRequired,
  isDeposit: PropTypes.bool,
};
MoneyButtonWrapper.defaultProps = {
  disabled: false,
  hidden: false,
  isDeposit: false,
};
